import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Colors } from '@zapier/design-system';
import { useInView } from 'react-intersection-observer';
import { ArticleHero } from 'components/ArticleHero/ArticleHero';
import { RichText } from 'components/RichText/RichText';
import { AuthorFeature } from 'components/AuthorFeature/AuthorFeature';
import { Article as ArticleType } from 'data/SingleArticle/types';
import { UpNext } from 'components/Cards/UpNext/UpNext';
import { FloatingPopup } from 'components/Popups/FloatingPopup';
import { SubscribePopup } from 'components/Popups/Subscribe/SubscribePopup';
import { SignupPopup } from 'components/Popups/Signup/SignupPopup';
import {
  conditionalPopupCategories,
  generalPopupsNameIds,
} from 'constants/popups';
import { mediaQueries } from 'components/mediaQueries';
import { SubscribeMiniContainer } from 'components/Subscribe/Mini/SubscribeMini';
import { Spacer } from 'components/Spacer/Spacer';
import { Pills } from 'components/Tags/Pills';
import paths from 'constants/paths';
import { useCurrentAccountId } from '@zapier/identity';
import { ArticleProgressBar } from 'components/ArticleProgressBar/ArticleProgressBar';
import { GuidedRecipeShort } from 'data/Article/types';
import { GeneralCTAInfo } from 'rest/contentful/types';
import { getGeneralCtaInfo } from 'data/CTA/data';
import { useUserSettingsContext } from 'data/User/UserSettingsContext';

type Props = {
  article: ArticleType;
  guidedRecipes: GuidedRecipeShort[];
  sendUpNextArticleSlugToParent: (upNextArticleSlug: string) => void;
};

export default function Article({
  article,
  sendUpNextArticleSlugToParent,
  guidedRecipes,
}: Props) {
  const [upNextArticleSlug, setUpNextArticleSlug] = useState('');
  const [subscribeRef, subscribeRefInView] = useInView({
    fallbackInView: false,
  });
  const [popupIsVisible, setPopupIsVisible] = useState(false);
  // initialInView needed here since react-intersection-observer doesn't track heroRef while nav items are fetched on client-side.
  // That leads to <TitleBanner/> being visible for a moment but it shouldn't be.
  const [heroRef, heroRefInView] = useInView({
    initialInView: true,
    fallbackInView: false,
  });
  const [articleBodyRef, articleBodyRefInView] = useInView({
    fallbackInView: false,
  });
  const { body, author, upNextArticles } = article;
  const currentAccountId = useCurrentAccountId();
  const isAuthenticated = currentAccountId && !isNaN(currentAccountId);
  const userContext = useUserSettingsContext();
  const [ctaInfo, setCtaInfo] = useState<GeneralCTAInfo | null>(null);
  const [ctaName, setCtaName] = useState('');

  useEffect(() => {
    if (upNextArticleSlug) {
      sendUpNextArticleSlugToParent(upNextArticleSlug);
    }
  }, [upNextArticleSlug, sendUpNextArticleSlugToParent]);

  useEffect(() => {
    const isSubscriberCtaRequired = article.categories.some((category) =>
      conditionalPopupCategories.SubscribePopup.includes(category.title),
    );

    if (heroRefInView || subscribeRefInView || !articleBodyRefInView) {
      setPopupIsVisible(false);
    } else if (
      (userContext?.ctaSignupEnabled && !isSubscriberCtaRequired) ||
      (isSubscriberCtaRequired && userContext?.ctaSubscribeEnabled)
    ) {
      setPopupIsVisible(true);
    }
  }, [
    heroRefInView,
    subscribeRefInView,
    articleBodyRefInView,
    article.categories,
    userContext?.ctaSignupEnabled,
    userContext?.ctaSubscribeEnabled,
  ]);

  useEffect(() => {
    const getCta = async () => {
      const ctaName = article.categories.some((category) =>
        conditionalPopupCategories.SubscribePopup.includes(category.title),
      )
        ? generalPopupsNameIds.SUBSCRIBE
        : generalPopupsNameIds.SIGN_UP;
      setCtaName(ctaName);
      const ctaInfo = await getGeneralCtaInfo(ctaName);
      setCtaInfo(ctaInfo);
    };
    getCta();
  }, [article]);

  const closeSubscribePopupTillNextSession = (isVisible: boolean) => {
    if (userContext?.ctaSubscribeEnabled) {
      userContext.setCtaSubscribeEnabled(isVisible);
      setPopupIsVisible(isVisible);
    }
  };

  const closeSignupPopupTillNextSession = (isVisible: boolean) => {
    if (userContext?.ctaSignupEnabled) {
      userContext.setCtaSignupEnabled(isVisible);
      setPopupIsVisible(isVisible);
    }
  };

  const renderConditionalPopup = () => {
    if (ctaInfo && ctaName === generalPopupsNameIds.SUBSCRIBE) {
      return (
        userContext?.ctaSubscribeEnabled && (
          <FloatingPopup
            isVisible={popupIsVisible}
            setIsVisible={closeSubscribePopupTillNextSession}
          >
            <SubscribePopup ctaInfo={ctaInfo as GeneralCTAInfo} />
          </FloatingPopup>
        )
      );
    } else if (
      ctaInfo &&
      !isAuthenticated &&
      ctaName === generalPopupsNameIds.SIGN_UP
    ) {
      return (
        <FloatingPopup
          isVisible={popupIsVisible}
          setIsVisible={closeSignupPopupTillNextSession}
        >
          <SignupPopup ctaInfo={ctaInfo as GeneralCTAInfo} />
        </FloatingPopup>
      );
    }
  };

  /**
   * Filter out tags and mentioned apps that don't have slugs.
   * Not all tags have slugs. A tag having a slug means it should be user-facing / clickable.
   * Arguably this could happen higher up in the data layer or down lower in Pills, but this seems fine for now.
   */
  const articleTags = article.tags.filter((tag) => Boolean(tag.slug));
  const articleMentionedApps = article.integrationsMentioned.filter((app) =>
    Boolean(app.slug),
  );

  return (
    <Outer>
      <ArticleProgressBar />
      <ArticleWrapper>
        <div ref={heroRef}>
          <ArticleHero article={article} />
        </div>
        <ArticleBodyWrapper>
          <div ref={articleBodyRef}>
            <RichText
              body={body}
              persona={article.personas[0]}
              guidedRecipes={guidedRecipes}
            />
          </div>
          <Spacer hasLine={true} />
          <div ref={subscribeRef}>
            <SubscribeMiniContainer
              noPadding={true}
              sectionToTrack="ArticlePageSubscribeArticleBottom"
            />
          </div>
          <Spacer hasLine={true} />
          <AuthorWrapper>
            {author.map((authorObj, index) => (
              <AuthorFeature key={`author-${index}`} author={authorObj} />
            ))}
          </AuthorWrapper>
          <Spacer hasLine={true} />

          <PillsContainer>
            {articleTags.length > 0 && (
              <Pills
                title="tags"
                items={articleTags.map((tag) => ({
                  slug: paths.tagsPage(tag.slug, 1),
                  title: tag.title,
                }))}
              />
            )}
            {articleMentionedApps.length > 0 && (
              <Pills
                title="mentioned apps"
                items={articleMentionedApps.map((app) => ({
                  slug: paths.appsPage(app.slug, 1),
                  title: app.title,
                }))}
              />
            )}
          </PillsContainer>
          <Spacer hasLine={true} />

          {/**
           * It seems that the `UpNext` article is not being fully re-rendered,
           * which means that the hook is not being fired a second time.
           * That's why the UpNext component is being forced to re-render by changing the
           * key based on the current article slug.
           *
           * https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
           */}
          <UpNext
            article={upNextArticles[0]}
            onUpNextArticleSlugReady={setUpNextArticleSlug}
            slugToExclude={article.slug}
            key={article.slug}
          />
        </ArticleBodyWrapper>
      </ArticleWrapper>
      {renderConditionalPopup()}
    </Outer>
  );
}

const Outer = styled.div`
  grid-template-columns: 1fr;
  display: grid;
  justify-content: center;
  place-items: center;
  position: relative;
  width: 100%;
  margin-top: 20px;
  ${mediaQueries.tablet} {
    margin-top: 0;
  }
`;

const ArticleWrapper = styled.div`
  padding: 0 20px;

  ${mediaQueries.tablet} {
    max-width: 600px;
    padding: 0;
  }
  ${mediaQueries.desktop} {
    max-width: 760px;
  }
`;

const ArticleBodyWrapper = styled.div`
  hr {
    border: 1px solid ${Colors.neutral300};
  }

  ${mediaQueries.desktop} {
    max-width: 700px;
    margin: 0 auto;
  }
`;

const AuthorWrapper = styled.div`
  > * {
    padding 10px 0;
  }
  > *:not(:last-child) {
    border-bottom: 1px solid #f2f2f2;
  }
  > *:last-child {
    padding-bottom: 0;
  }

  > *:first-child {
    padding-top: 0;
  }
`;

const PillsContainer = styled.div`
  display: grid;
  gap: 20px;
  margin-top: 5px;
  margin-bottom: 5px;
`;
